import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Copy, Lock, Sparkles, Users, Check, Share2, Download, Crown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Referral() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const loadAll = async () => {
    try {
      const [r, lb, prefs] = await Promise.all([
        api.get("/referral/me"),
        api.get("/referral/leaderboard"),
        api.get("/me/preferences"),
      ]);
      setInfo(r.data);
      setLeaderboard(lb.data);
      setVisible(prefs.data.referral_visible !== false);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const toggleVisibility = async (next) => {
    setVisible(next);
    try {
      await api.patch("/me/preferences", { referral_visible: next });
      const { data } = await api.get("/referral/leaderboard");
      setLeaderboard(data);
      toast.success(next ? "Tu apparais dans le classement" : "Tu apparais en Anonyme");
    } catch {
      toast.error("Mise à jour impossible");
      setVisible(!next);
    }
  };

  const copyLink = async () => {
    if (!info?.link) return;
    try {
      await navigator.clipboard.writeText(info.link);
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const share = async () => {
    if (navigator.share && info?.link) {
      try {
        await navigator.share({
          title: "Student Hub",
          text: "Toutes mes démarches étudiantes en un seul endroit. Inscris-toi avec mon lien :",
          url: info.link,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const downloadGuide = async () => {
    try {
      const resp = await api.get("/referral/guide", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guide-etudiant-france.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Guide téléchargé");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Téléchargement impossible");
    }
  };

  if (loading) return <div className="text-slate-500" data-testid="referral-loading">Chargement…</div>;
  if (!info) return <div className="text-slate-500">Aucune donnée.</div>;

  const pct = Math.min(100, Math.round((info.count / info.threshold) * 100));

  return (
    <div className="space-y-10" data-testid="referral-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Parraine tes potes</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Ton espace parrainage</h1>
        <p className="text-slate-600 mt-3 max-w-2xl">
          Partage Student Hub avec tes potes étudiants. À <strong>{info.threshold} parrainages</strong>, tu débloques le
          <strong> Guide Étudiant en France</strong> au format PDF (logement, aides, démarches, bons plans).
        </p>
      </header>

      {/* Link card */}
      <section className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="referral-link-card">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ton code</p>
        <p className="font-display text-3xl font-bold text-slate-900 mt-1 tracking-widest" data-testid="referral-code">
          {info.code}
        </p>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-5">Ton lien à partager</p>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <input
            readOnly
            value={info.link}
            data-testid="referral-link-input"
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 text-sm focus:outline-none"
            onFocus={(e) => e.target.select()}
          />
          <Button onClick={copyLink} className="bg-slate-900 hover:bg-slate-800 h-11 px-5" data-testid="copy-link-btn">
            {copied ? <Check size={16} /> : <Copy size={16} />}
            <span className="ml-2">{copied ? "Copié" : "Copier"}</span>
          </Button>
          <Button onClick={share} variant="outline" className="h-11 px-5" data-testid="share-btn">
            <Share2 size={16} className="mr-2" /> Partager
          </Button>
        </div>
      </section>

      {/* Progress + Guide */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="referral-progress-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Filleuls inscrits</p>
              <p className="font-display text-4xl font-bold text-slate-900 mt-1" data-testid="referral-count">
                {info.count} <span className="text-slate-300">/ {info.threshold}</span>
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Users size={22} />
            </div>
          </div>
          <div className="mt-5 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {info.referred.length === 0 ? (
            <p className="text-slate-500 text-sm mt-5">Personne pour le moment. Partage ton lien ci-dessus !</p>
          ) : (
            <ul className="mt-5 space-y-2 max-h-60 overflow-y-auto pr-1">
              {info.referred.map((r, idx) => (
                <li key={idx} data-testid={`referred-${idx}`}
                    className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-xs">
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="text-slate-700 font-medium">{r.name}</span>
                  </div>
                  {r.joined_at && (
                    <span className="text-slate-400 text-xs">
                      {new Date(r.joined_at).toLocaleDateString("fr-FR")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={`rounded-2xl border p-6 sm:p-8 ${
            info.unlocked ? "bg-yellow-50 border-yellow-200" : "bg-white border-slate-200"
          }`}
          data-testid="guide-card"
        >
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                info.unlocked ? "bg-yellow-300 text-slate-900" : "bg-slate-100 text-slate-400"
              }`}>
              {info.unlocked ? <Sparkles size={20} /> : <Lock size={20} />}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Récompense</p>
              <h3 className="font-display text-xl font-bold text-slate-900 mt-1">Guide Étudiant en France (PDF)</h3>
              <p className="text-slate-600 text-sm mt-2">
                Le condensé Student Hub : ton dossier de location, les erreurs à éviter, les aides à activer, la santé,
                les réductions et les conseils pour étudiants internationaux.
              </p>
              {info.unlocked ? (
                <Button onClick={downloadGuide} className="mt-5 bg-slate-900 hover:bg-slate-800" data-testid="download-guide-btn">
                  <Download size={16} className="mr-2" /> Télécharger le guide
                </Button>
              ) : (
                <p className="text-sm text-slate-500 mt-5">
                  Encore <strong>{info.threshold - info.count}</strong> parrainage(s) pour le débloquer.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="space-y-5" data-testid="leaderboard-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-300 text-slate-900 flex items-center justify-center">
              <Crown size={20} />
            </div>
            <div>
              <h2 className="font-display text-2xl font-semibold text-slate-900">Top 10 ambassadeurs</h2>
              <p className="text-slate-500 text-sm">Les étudiants qui ramènent le plus de filleuls.</p>
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5"
                 data-testid="leaderboard-visibility">
            {visible ? <Eye size={16} className="text-indigo-500" /> : <EyeOff size={16} className="text-slate-400" />}
            <span className="text-sm text-slate-700">Apparaître avec mon pseudo</span>
            <Switch checked={visible} onCheckedChange={toggleVisibility} data-testid="visibility-toggle" />
          </label>
        </div>
        {leaderboard.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-8 text-center text-slate-400" data-testid="leaderboard-empty">
            Aucun ambassadeur encore. Sois le premier !
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <ul>
              {leaderboard.map((row) => (
                <li
                  key={row.rank}
                  data-testid={`leaderboard-row-${row.rank}`}
                  className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <span
                      className={`w-9 h-9 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0 ${
                        row.rank === 1 ? "bg-yellow-200 text-yellow-900"
                          : row.rank === 2 ? "bg-slate-200 text-slate-700"
                          : row.rank === 3 ? "bg-orange-200 text-orange-800"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {row.rank}
                    </span>
                    <span className={`truncate font-medium ${row.name === "Anonyme" ? "text-slate-400 italic" : "text-slate-900"}`}>
                      {row.name}
                    </span>
                  </div>
                  <span className="text-sm text-slate-600">
                    <strong className="text-indigo-600">{row.count}</strong> filleul{row.count > 1 ? "s" : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
